/**
 * Iframe del dashboard externo de un reporte (Looker, Data Studio, estudio Médico Digital...).
 * Si el reporte es el estudio Médico Digital, antes de montar el iframe pide al backend un pase de
 * acceso (POST /api/sso/estudio/:reportId) y lo agrega en el fragmento de la URL (#sso=...): el
 * estudio lo canjea por su propia sesión y el usuario no vuelve a escribir sus credenciales. El
 * pase dura 60 s y es de un solo uso, así que se pide uno nuevo cada vez que se monta el iframe.
 */
import React, { useEffect, useState } from 'react';
import { auth } from '../firebaseConfig';
import { isEstudioUrl } from '../reportViewUtils';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8081';

/** Pide el pase del estudio para el reporte y devuelve la URL del iframe con el pase en el fragmento. */
async function urlEstudioConPase(report) {
    const user = auth.currentUser;
    if (!user) throw new Error('Inicia sesión para abrir el estudio.');
    const token = await user.getIdToken();
    const response = await fetch(`${API_URL}/api/sso/estudio/${encodeURIComponent(report.id)}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data.pase) {
        throw new Error(data.error || 'No se pudo abrir el estudio.');
    }
    const base = String(report.dashboardUrl).trim().split('#')[0];
    return `${base}#sso=${encodeURIComponent(data.pase)}`;
}

/**
 * Props: `report` ({ id, dashboardUrl }) y el resto se pasa tal cual al <iframe> (style, title...).
 * `mensajeStyle` es el estilo del texto mientras se abre el estudio o si falla.
 */
export default function ReportEmbedFrame({ report, mensajeStyle, title, ...iframeProps }) {
    const esEstudio = isEstudioUrl(report?.dashboardUrl) && !!report?.id;
    const [src, setSrc] = useState(esEstudio ? null : report?.dashboardUrl);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!esEstudio) {
            setSrc(report?.dashboardUrl);
            return undefined;
        }
        let activo = true;
        setSrc(null);
        setError(null);
        urlEstudioConPase(report)
            .then((url) => { if (activo) setSrc(url); })
            .catch((err) => { if (activo) setError(err.message); });
        return () => { activo = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [esEstudio, report?.id, report?.dashboardUrl]);

    const estiloMensaje = mensajeStyle || { padding: '24px', color: '#5f6368' };
    if (error) return <div style={estiloMensaje}>{error}</div>;
    if (!src) return <div style={estiloMensaje}>Abriendo estudio...</div>;
    return <iframe src={src} title={title || 'Dashboard'} {...iframeProps} />;
}
